import React, { useState, useEffect, useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

import keyValueReducer from '@chronos/ui-toolkit/reducers/keyValueReducer';
import { setState } from '@chronos/ui-toolkit/actions';

import { Calendar } from 'react-date-range';
import Button from '../Button';
import Popup from '../Popup';

import 'react-date-range/dist/styles.css';
import './DateTimePickerTheme.css';
import * as S from './styled';

const minutes = [
  '00',
  '05',
  '10',
  '15',
  '20',
  '25',
  '30',
  '35',
  '40',
  '45',
  '50',
  '55',
  '60',
];
const hours = [
  '00',
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
  '18',
  '19',
  '20',
  '21',
  '22',
  '23',
].reverse();

const initialState = {
  hour: null,
  minute: null,
  dateStr: '',
  timeStr: '',
};

const DateTimePicker = ({
  dateTime,
  withTime,
  maxDate,
  minDate,
  months,
  place,
  appendTarget,
  direction,
  onClose,
  onChange,
  readOnly,
  weekStartsOn,
}) => {
  const [state, dispatch] = useReducer(keyValueReducer, initialState);
  const [isOpen, setIsOpen] = useState(false);

  const handleOuterAction = useCallback(() => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (dateTime) {
      dispatch(
        setState({
          dateStr: format(dateTime, 'dd/MM/yyyy'),
          timeStr: format(dateTime, 'HH:mm'),
        })
      );
    }
  }, [dateTime]);

  useEffect(() => {
    if (isOpen) {
      const hour = dateTime.getHours();
      const minute = Math.round(dateTime.getMinutes() / 5) * 5;
      dispatch(
        setState({
          hour,
          minute,
        })
      );
      const hourEl = document.querySelector(`#hour-${hour}`);
      const minuteEl = document.querySelector(`#minute-${minute}`);
      if (hourEl) {
        hourEl.scrollIntoView({ behavior: 'auto', block: 'center' });
      }
      if (minuteEl) {
        minuteEl.scrollIntoView({ behavior: 'auto', block: 'center' });
      }
    }
  }, [isOpen, dateTime]);

  return (
    <S.DateTimePicker>
      <Popup
        isOpen={isOpen}
        handleOuterAction={handleOuterAction}
        setPopupVisible={() => {
          setIsOpen(true);
        }}
        zIndex={9999}
        place={place}
        appendTarget={appendTarget}
        trigger={
          <S.DatePickerTrigger isOpen={isOpen}>
            <S.DateSide>
              <S.Input
                mask="99/99/9999"
                readOnly={readOnly}
                value={state.dateStr}
                onFocus={(ev) => {
                  ev.target.select();
                }}
                onChange={(ev) => {
                  const { value } = ev.target;
                  dispatch(
                    setState({
                      dateStr: value,
                    })
                  );
                  function isValidDate(d) {
                    return d instanceof Date && !Number.isNaN(Number(d));
                  }
                  if (!value.includes('_')) {
                    const [day, month, year] = value.split('/');
                    const newDateTime = new Date(`${month}/${day}/${year}`);
                    const [hour, minute] = state.timeStr.split(':');
                    newDateTime.setHours(parseInt(hour, 10));
                    newDateTime.setMinutes(parseInt(minute, 10));
                    if (isValidDate(newDateTime)) {
                      onChange(newDateTime);
                    } else {
                      onChange(new Date(dateTime));
                    }
                  }
                }}
              />
            </S.DateSide>
            {withTime && (
              <S.TimeSide>
                <S.Input
                  mask={[/[0-2]/, /[0-9]/, ':', /[0-5]/, /[0-9]/]}
                  value={state.timeStr}
                  onFocus={(ev) => {
                    ev.target.select();
                  }}
                  onChange={(ev) => {
                    const { value } = ev.target;
                    dispatch(
                      setState({
                        timeStr: value,
                      })
                    );
                    const [hour, minute] = value.split(':');
                    if (!value.includes('_')) {
                      const newDateTime = new Date(dateTime);
                      newDateTime.setHours(parseInt(hour, 10));
                      newDateTime.setMinutes(parseInt(minute, 10));
                      onChange(newDateTime);
                    }
                  }}
                />
              </S.TimeSide>
            )}
          </S.DatePickerTrigger>
        }
      >
        <S.Calendar>
          <Calendar
            className="date-time-picker"
            weekStartsOn={weekStartsOn}
            date={dateTime}
            maxDate={maxDate}
            minDate={minDate}
            months={months}
            direction={direction}
            onChange={(newDateTime) => {
              const [hour, minute] = state.timeStr.split(':');
              newDateTime.setHours(parseInt(hour, 10));
              newDateTime.setMinutes(parseInt(minute, 10));
              onChange(newDateTime);
            }}
          />
          {withTime && (
            <S.TimePicker>
              <S.TimeList>
                {hours.map((hour) => (
                  <Button
                    key={hour}
                    id={`hour-${hour}`}
                    isSelected={state.hours === parseInt(hour, 10)}
                    appearance="subtle-link"
                    onClick={() => {
                      const newDateTime = new Date(dateTime);
                      newDateTime.setHours(parseInt(hour, 10));
                      onChange(newDateTime);
                    }}
                  >
                    {hour}
                  </Button>
                ))}
              </S.TimeList>
              <S.TimeList>
                {minutes.map((minute) => (
                  <Button
                    key={minute}
                    id={`minute-${minute}`}
                    isSelected={
                      Math.round(state.minute / 5) * 5 === parseInt(minute, 10)
                    }
                    appearance="subtle-link"
                    onClick={() => {
                      const newDateTime = new Date(dateTime);
                      newDateTime.setMinutes(parseInt(minute, 10));
                      onChange(newDateTime);
                    }}
                  >
                    {minute}
                  </Button>
                ))}
              </S.TimeList>
            </S.TimePicker>
          )}
        </S.Calendar>
      </Popup>
    </S.DateTimePicker>
  );
};

DateTimePicker.propTypes = {
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  dateTime: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  place: PropTypes.string,
  weekStartsOn: PropTypes.number,
  /* How many months to display */
  months: PropTypes.number,
  withTime: PropTypes.bool,
  direction: PropTypes.string,
  readOnly: PropTypes.bool,
};

DateTimePicker.defaultProps = {
  weekStartsOn: 0,
  months: 1,
  place: 'above',
  direction: 'vertical',
  withTime: true,
  readOnly: false,
};

export default DateTimePicker;
